import { create } from 'zustand'
import type { QueryClient } from '@tanstack/react-query'
import {
  ApiError,
  OfflineError,
  type Changeset,
  type ConflictField,
  type PhotographChangeset,
  type SyncResult,
  type UpdateChangeset,
} from '@mads/shared'
import { api } from './session'
import { useQueueStore } from './queue'
import { readQueuedPhoto } from './photoStore'
import { queryKeys } from './queries'

/**
 * One queued change the server would not simply accept, paired with what it
 * said about it — which is what the resolver walks.
 */
export interface Unresolved {
  changeset: Changeset
  result: SyncResult
}

type SyncPhase =
  | 'idle'
  /** Talking to the API. The app is blocked while this runs. */
  | 'syncing'
  /** Waiting for the walker to answer. The app stays blocked. */
  | 'resolving'
  /** No connection, or the API refused the whole batch. Nothing is lost. */
  | 'failed'

interface SyncState {
  phase: SyncPhase
  unresolved: Unresolved[]
  /** A sentence about the batch as a whole, not about one change. */
  error: string | null
  applied: number
  /**
   * What each client id turned out to be — the row the server made for it
   * (issue #70).
   *
   * The batch has always answered with this so photographs can find their
   * record; keeping it lets a *screen* find one too. Somebody correcting a
   * lantern they created in a cellar is sitting on `/inventory/-17…` when the
   * signal comes back, and that record is about to stop existing under that
   * name; without the mapping the only honest move is to bounce them to the
   * list, which loses the lantern they were looking at.
   *
   * In memory only. It is worth exactly one navigation, and a reload has taken
   * them somewhere else anyway.
   */
  resolved: Record<string, number>
}

interface SyncActions {
  run: (queryClient: QueryClient) => Promise<void>
  keepMine: (changesetId: string, field: string) => Promise<void>
  keepAllMine: (changesetId: string) => Promise<void>
  keepTheirs: (changesetId: string, field: string) => Promise<void>
  discard: (changesetId: string) => Promise<void>
  /**
   * Keep this change and stop asking about it for now (issue #64, step 3) — the
   * answer to a walk that met another one already open. It stays queued and
   * goes on being sent; what changes is that the same refusal no longer blocks
   * the app, so the walker can carry on counting until the other walk ends.
   */
  keepForLater: (changesetId: string) => Promise<void>
  dismissError: () => void
}

/**
 * The replay loop, and the block it puts the app behind (issue #64, step 2).
 *
 * When a device reconnects holding a queue, everything else in the app goes
 * away until the queue has been dealt with: a half-synced inventory is a worse
 * thing to browse than none, and somebody scrolling a list that is about to
 * change under them cannot make a good decision about anything.
 *
 * The loop branches on the status codes the API already means precisely — 401
 * stop and re-authenticate, 403 stop, 429 back off — and **none of them
 * discards the queue**. Being refused is not a reason to throw away somebody's
 * counts.
 */
export const useSyncStore = create<SyncState & SyncActions>()((setState, getState) => ({
  phase: 'idle',
  unresolved: [],
  error: null,
  applied: 0,
  resolved: {},

  run: async (queryClient) => {
    const { phase } = getState()

    // One at a time. A second run while the first is in flight would send the
    // same changesets twice — survivable, because replay is idempotent, but it
    // would also race two rewrites of the queue against each other.
    if (phase === 'syncing') return

    const queued = useQueueStore.getState().changesets

    if (queued.length === 0) {
      setState({ phase: 'idle', unresolved: [], error: null })
      return
    }

    setState({ phase: 'syncing', error: null })

    // **One queue, two transports** (issue #64, step 4). Everything that is
    // JSON goes as one batch, because the server has to see it as a single unit
    // of work — an item created into a location created five minutes earlier
    // only resolves if both are in front of it. A photograph is bytes, so it
    // goes through the image endpoints that already exist, one request each,
    // and it goes *after*: an item created with no connection has no id until
    // the batch answers with one.
    const batch = queued.filter((changeset) => changeset.kind !== 'photograph')
    const photographs = queued.filter(
      (changeset): changeset is PhotographChangeset => changeset.kind === 'photograph',
    )

    let results: readonly SyncResult[] = []

    if (batch.length > 0) {
      // Before the send, not after (issue #70). Anything that has been to the
      // server may be in `sync_record` already — a batch that applied and whose
      // answer was lost is the case that table exists for — and a changeset in
      // that position must never have a later edit folded into it, because the
      // repeat would be answered with the old result and the new edit dropped.
      await useQueueStore.getState().markSent(batch.map((changeset) => changeset.id))

      try {
        results = (await api.sync(batch)).results
      } catch (error) {
        setState({
          phase: 'failed',
          error: messageFor(error),
          // Kept, every time. A queue survives a refusal, an outage and a
          // signed-out session alike; the only thing that removes a change is
          // it applying, or somebody deliberately discarding it.
        })
        return
      }
    }

    const byId = new Map(results.map((result) => [result.changesetId, result]))
    const deferred = new Set(useQueueStore.getState().deferred)
    const applied = new Set<string>()
    const unresolved: Unresolved[] = []

    for (const changeset of queued) {
      const result = byId.get(changeset.id)

      if (result === undefined) continue

      if (result.status === 'applied') {
        applied.add(changeset.id)
        continue
      }

      // A block is not something to ask about: it is waiting for the change
      // above it, and answering that one releases it.
      if (result.status === 'blocked') continue

      // A refusal the walker has already been told about and asked to keep
      // (issue #64, step 3). It is still being sent — the open walk it met will
      // be finished, and then it applies — but hearing the same answer again is
      // not a reason to put the whole app behind the block a second time.
      if (deferred.has(changeset.id) && result.code !== undefined) continue

      unresolved.push({ changeset, result })
    }

    // The photographs, now that the batch has handed back the ids anything
    // created offline was waiting for.
    const ids = idsFrom(results)
    const photoOutcome = await sendPhotographs(photographs, ids)

    for (const id of photoOutcome.applied) applied.add(id)
    unresolved.push(...photoOutcome.unresolved)

    await useQueueStore
      .getState()
      .replace(queued.filter((changeset) => !applied.has(changeset.id)))

    // Everything the walk touched is now the server's answer rather than this
    // device's guess, so the provisional records go with one invalidation.
    await queryClient.invalidateQueries()

    // Except these, which invalidation cannot reach: a `['item', -17…]` entry
    // is disabled (`useItem` guards on `id > 0`), so marking it stale never
    // refetches it, and it would otherwise be written to the device's cache
    // forever — one dead entry per record ever created in a cellar. Housekeeping
    // rather than the thing that moves a screen along: dropping a query does not
    // re-render an observer that still holds it, which is why `EquipmentDetail`
    // follows `resolved` instead.
    queryClient.removeQueries({
      predicate: (query) =>
        query.queryKey[0] === 'item' &&
        typeof query.queryKey[1] === 'number' &&
        query.queryKey[1] < 0,
    })

    setState({
      phase:
        unresolved.length > 0 ? 'resolving' : photoOutcome.error === null ? 'idle' : 'failed',
      unresolved,
      applied: applied.size,
      error: photoOutcome.error,
      resolved: { ...getState().resolved, ...Object.fromEntries(ids) },
    })
  },

  /**
   * Keep this device's value. The changeset is re-sent with its base moved to
   * what the server said, which makes it an ordinary application — and named in
   * `resolved` so the trail records that a disagreement was settled rather than
   * that a value happened to match.
   */
  keepMine: async (changesetId, field) => {
    await answer(setState, getState, changesetId, [field])
  },

  keepAllMine: async (changesetId) => {
    const entry = getState().unresolved.find(
      (candidate) => candidate.changeset.id === changesetId,
    )

    await answer(
      setState,
      getState,
      changesetId,
      (entry?.result.conflicts ?? []).map((conflict) => conflict.field),
    )
  },

  /**
   * Keep theirs: the field is dropped from the changeset, so replay never
   * carries it. A changeset with nothing left to say is dropped whole.
   */
  keepTheirs: async (changesetId, field) => {
    const { unresolved } = getState()
    const entry = unresolved.find((candidate) => candidate.changeset.id === changesetId)

    if (entry === undefined || entry.changeset.kind !== 'update') return

    const fields = { ...entry.changeset.fields }
    delete fields[field]

    if (Object.keys(fields).length === 0) {
      await getState().discard(changesetId)
      return
    }

    const changeset: UpdateChangeset = { ...entry.changeset, fields }

    setState({
      unresolved: unresolved.map((candidate) =>
        candidate.changeset.id === changesetId ? { ...candidate, changeset } : candidate,
      ),
    })

    await replaceInQueue(changeset)
  },

  discard: async (changesetId) => {
    const entry = getState().unresolved.find(
      (candidate) => candidate.changeset.id === changesetId,
    )
    const queue = useQueueStore.getState()
    const changeset = entry?.changeset

    // Discarding the *start* of a walk takes its counts and its finish with it.
    // Leaving them behind would leave changesets pointing at a record that is
    // never going to exist, which is a queue that can never empty.
    if (changeset?.entity === 'stocktake' && changeset.kind === 'create') {
      await queue.discardWalk(changeset.clientId)
    } else {
      await queue.discard(changesetId)
    }

    // Whatever survived, which for a walk is fewer than one fewer.
    const remaining = new Set(useQueueStore.getState().changesets.map((queued) => queued.id))

    setState({
      unresolved: getState().unresolved.filter((candidate) =>
        remaining.has(candidate.changeset.id),
      ),
    })
  },

  keepForLater: async (changesetId) => {
    await useQueueStore.getState().defer(changesetId)

    setState({
      unresolved: getState().unresolved.filter((entry) => entry.changeset.id !== changesetId),
    })
  },

  dismissError: () => setState({ phase: 'idle', error: null }),
}))

/** What the batch handed back, so a photograph can find the row it belongs to. */
function idsFrom(results: readonly SyncResult[]): Map<string, number> {
  const map = new Map<string, number>()

  for (const result of results) {
    if (result.clientId !== undefined && result.recordId !== undefined) {
      map.set(result.clientId, result.recordId)
    }
  }

  return map
}

interface PhotoOutcome {
  applied: string[]
  unresolved: Unresolved[]
  /** A sentence about the photographs as a whole, when none of them could go. */
  error: string | null
}

/**
 * Sends each queued photograph through the image endpoints (issue #64,
 * step 4).
 *
 * One request each, in the order they were taken, and **the first outage stops
 * the rest**: they are all going to the same bucket, so the second would fail
 * for the reason the first did, and thirty pointless uploads over a connection
 * that has just proved unreliable is the opposite of what a walker needs. What
 * is refused is different — a file the server will not store is about that file
 * and nothing else, so the rest carry on.
 */
async function sendPhotographs(
  photographs: readonly PhotographChangeset[],
  ids: ReadonlyMap<string, number>,
): Promise<PhotoOutcome> {
  const outcome: PhotoOutcome = { applied: [], unresolved: [], error: null }
  const { userId } = useQueueStore.getState()

  if (photographs.length === 0 || userId === null) return outcome

  for (const changeset of photographs) {
    const id = 'id' in changeset.target ? changeset.target.id : ids.get(changeset.target.clientId)

    if (id === undefined) {
      // Its record's create has not landed — a conflict being argued about, or
      // a refusal. It waits, exactly as a blocked changeset in the batch does.
      continue
    }

    try {
      await useQueueStore.getState().markSent([changeset.id])
      await sendOne(changeset, id, userId)
      outcome.applied.push(changeset.id)
    } catch (error) {
      if (isOutage(error)) {
        outcome.error = messageFor(error)
        break
      }

      outcome.unresolved.push({
        changeset,
        result: {
          changesetId: changeset.id,
          status: 'rejected',
          reason: reasonFor(error),
        },
      })
    }
  }

  return outcome
}

async function sendOne(
  changeset: PhotographChangeset,
  id: number,
  userId: number,
): Promise<void> {
  const at = changeset.at

  if (changeset.action === 'clear') {
    await (changeset.entity === 'item'
      ? api.removeItemImage(id, at)
      : api.removeFixtureModelImage(id, at))

    return
  }

  const photo = await readQueuedPhoto(userId, changeset.id)

  if (photo === undefined) {
    // The blob is gone and the changeset is not. Nothing can send this, and
    // saying so is the only honest move — a silent drop would be a photograph
    // somebody believes is on the record.
    throw new ApiError(422, 'That photograph is no longer on this device.')
  }

  await (changeset.entity === 'item'
    ? api.setItemImage(id, photo, at)
    : api.setFixtureModelImage(id, photo, at))
}

/**
 * Whether this is our fault rather than the photograph's.
 *
 * A bucket that will not answer is a 503 by design (`ImageController`), and a
 * dead connection is an `OfflineError` — neither is a reason to make somebody
 * look at a picture and decide something about it. 401, 403 and 429 are the
 * same: the batch already treats them as "stop and keep", and a photograph is
 * no different.
 */
function isOutage(error: unknown): boolean {
  if (error instanceof OfflineError) return true

  return error instanceof ApiError && [401, 403, 429, 503].includes(error.status)
}

function reasonFor(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) {
    return 'The record this photograph belongs to has been binned or destroyed since it was taken.'
  }

  return error instanceof ApiError ? error.message : 'That photograph could not be sent.'
}

/**
 * Moves the named fields' bases to what the server holds, so the next replay
 * sees a changeset that agrees about where it started.
 */
function withBaseMoved(
  changeset: Changeset,
  fields: readonly string[],
  result: SyncResult,
): Changeset {
  if (changeset.kind !== 'update') return changeset

  const conflicts = new Map((result.conflicts ?? []).map((conflict) => [conflict.field, conflict]))
  const next: Record<string, { base: unknown; value: unknown }> = { ...changeset.fields }

  for (const field of fields) {
    const conflict = conflicts.get(field)
    const existing = next[field]

    if (conflict === undefined || existing === undefined) continue

    next[field] = { base: conflict.theirs, value: existing.value }
  }

  const resolved = [...new Set([...(changeset.resolved ?? []), ...fields])]

  return { ...changeset, fields: next, resolved }
}

/**
 * Records an answer, in both places it has to live.
 *
 * The resolver's copy is what the screen redraws from; the *queue's* copy is
 * what the next replay actually sends. Updating only the first is how an
 * answered conflict comes straight back — the walker says "keep mine", the send
 * carries the old base, and the server disagrees with it all over again.
 */
async function answer(
  setState: (partial: Partial<SyncState>) => void,
  getState: () => SyncState & SyncActions,
  changesetId: string,
  fields: readonly string[],
): Promise<void> {
  const entry = getState().unresolved.find((candidate) => candidate.changeset.id === changesetId)

  if (entry === undefined || fields.length === 0) return

  const changeset = withBaseMoved(entry.changeset, fields, entry.result)

  setState({
    unresolved: getState().unresolved.map((candidate) =>
      candidate.changeset.id === changesetId ? { ...candidate, changeset } : candidate,
    ),
  })

  await replaceInQueue(changeset)
}

async function replaceInQueue(changeset: Changeset): Promise<void> {
  const queue = useQueueStore.getState()

  await queue.replace(
    queue.changesets.map((queued) => (queued.id === changeset.id ? changeset : queued)),
  )
}

/**
 * What went wrong with the batch as a whole. The codes are the ones the API
 * already means precisely, and none of them is a reason to lose the queue.
 */
function messageFor(error: unknown): string {
  if (error instanceof OfflineError) {
    return 'Still no connection. Your changes are safe on this device and will be sent when there is one.'
  }

  if (error instanceof ApiError) {
    if (error.status === 401) {
      return 'Your sign-in has expired. Sign in again and your changes will be sent.'
    }

    if (error.status === 403) {
      return 'This account can no longer make changes. Your changes are still here — speak to the Head of Tech.'
    }

    if (error.status === 429) {
      return 'The server asked us to slow down. Your changes are safe; try again in a minute.'
    }

    return error.message
  }

  return 'Your changes could not be sent. They are still on this device.'
}

/**
 * Whether the walker still has something to answer. Anything left in
 * `unresolved` blocks the app, because the alternative is browsing an inventory
 * that is half this device's and half the server's.
 */
export function useIsBlockedBySync(): boolean {
  return useSyncStore((s) => s.phase === 'syncing' || s.phase === 'resolving' || s.phase === 'failed')
}

/** The fields still in dispute on one changeset. */
export function conflictsOf(entry: Unresolved): readonly ConflictField[] {
  return entry.result.conflicts ?? []
}

/**
 * Everything a queued change touched, so a sync can invalidate exactly the
 * screens that could have moved. Exported for the tests, which is the only
 * place the shape of the invalidation is worth asserting.
 */
export const syncInvalidationKeys = [
  queryKeys.dashboard,
  queryKeys.locations,
  queryKeys.fixtureModels,
  queryKeys.autocomplete,
]
